import constraintsStore from 'shared/store/constraintsStore';
import { ConstraintMode } from 'types/ConstraintProfileTypes';
import { CONSTRAINTS } from '../constants';

interface BuildOptions {
  /**
   * Include the constraints mode key in the payload. Used by paths that
   * create a thread (createThreadAndStart from chat input, or Invia on a
   * fresh chat) — the backend needs MODE_KEY to know which mode the thread
   * is in. Subsequent writes on an existing thread don't need this.
   */
  includeMode?: ConstraintMode;
}

export interface ConstraintsSyncPayload {
  /**
   * The metadata dict to send to the server (alongside the message, or as
   * the thread-metadata argument to seedThread).
   *
   *   - METADATA_KEY: the working document (omitted when null).
   *   - APPLIED_PROFILE_KEY: { id, name } or {} for no profile (the backend
   *     rejects null metadata values; {} is the agreed sentinel).
   *   - MODE_KEY: present only when `includeMode` is set.
   */
  metadata: Record<string, unknown>;
}

/**
 * Single source of truth for every FE-initiated constraints write. Used by
 *
 *   - `useConstraintsSync` (silent seedThread after Reset/apply/predefiniti/Salva)
 *   - `useConstraintsMetadata` (Invia: continueThread / createThreadAndStart)
 *   - `ChatPanel.handleSendMessage` (first chat message on a fresh chat)
 *
 * Reads `workingDocument` and `appliedProfile` from `constraintsStore` at
 * call time so every writer ships a payload built from the same local
 * snapshot.
 */
export function buildConstraintsSyncPayload(opts: BuildOptions = {}): ConstraintsSyncPayload {
  const workingDocument = constraintsStore.getState().workingDocument;
  const appliedProfile = constraintsStore.getState().appliedProfile;
  const appliedProfileMeta = appliedProfile
    ? { id: appliedProfile.id, name: appliedProfile.name }
    : {};

  const metadata: Record<string, unknown> = {};
  if (workingDocument) {
    metadata[CONSTRAINTS.METADATA_KEY] = workingDocument;
    metadata[CONSTRAINTS.APPLIED_PROFILE_KEY] = appliedProfileMeta;
  }
  if (opts.includeMode) {
    metadata[CONSTRAINTS.MODE_KEY] = { mode: opts.includeMode };
  }

  return { metadata };
}
